import React from "react";
import PointingUpwards from "../../../../../images/donation/upwards-arrow-default.svg";
import styled from "styled-components";
import {
  DonationBeneficiaryModel,
  DonationCheckoutModel,
  DonationPricingPageInputsModel,
  ItemDetailInputModel,
  MediaModel,
} from "../models";
import {
  CustomLabel,
  FlexContainer,
  Input,
  IStyleProps,
  theme,
  UtilityMethods,
  validateDecimalValue,
} from "@mindmemobile/mm-shared-global";
import {
  DonationPricingOption,
  DonationType,
  ImagePlacementTypes,
} from "../enums";
import PricingOption from "./PricingOption";
import {
  AdvertisingBlockWrapper,
  CustomDonationButton,
  DonationPayButton,
  MembershipRightBar,
} from "../styles";
import CarouselComponent from "./CarouselComponent";
import { InputWrapper } from "./TableGrid";

type IProps = {
  pricing: DonationPricingPageInputsModel;
  detailSection: ItemDetailInputModel;
  donationOptions?: DonationCheckoutModel;
  updateDonationPricingPageData?: (
    data: DonationPricingPageInputsModel
  ) => void;
  onDonationClick?: () => void;
  className?: string;
  isPreview: boolean;
  isCustomerFacing?: boolean;
  scrollToField?: keyof DonationPricingPageInputsModel;
};
interface IstyledProps {
  bgColor?: string;
  defaultFont?: string;
  border?: string;
  primaryButton?: string;
}

const AdvertisingBlockMainWrapper = styled.div<IstyledProps>`
  /* border: ${(props) => props.border || "1px solid #aaa"}; */
  background: ${(props) => props.bgColor || "#fbfbfb"};
  overflow: unset;
  border-radius: 10px;
  height: inherit;
  color: ${(props) => props.defaultFont || "#555"};
  label,
  .styled-chkbox label {
    color: ${(props) => props.defaultFont || "#555"};
  }
  button {
    color: ${(props) => props.primaryButton || "#555"};
  }
`;

class DonationAdvertisingBlock extends React.Component<IProps> {
  previewRef: React.RefObject<HTMLDivElement>;
  constructor(props: IProps) {
    super(props);
    this.previewRef = React.createRef();
  }

  componentDidUpdate() {
    if (this.props.scrollToField) {
      this.scrollToField(this.props.scrollToField);
    }
  }

  scrollToField = (field: keyof DonationPricingPageInputsModel) => {
    if (!this.previewRef.current) return;
    const targetElement = this.previewRef.current.querySelector(
      `[data-field="${field}"]`
    );
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  onChange = (
    field: keyof DonationPricingPageInputsModel,
    value: boolean | string | DonationBeneficiaryModel[] | number
  ) => {
    this.props.updateDonationPricingPageData &&
      this.props.updateDonationPricingPageData(
        new DonationPricingPageInputsModel({
          ...this.props.pricing,
          [field]: value,
        })
      );
  };

  onCustomAmountChange = (amount: string) => {
    this.props.updateDonationPricingPageData &&
      this.props.updateDonationPricingPageData(
        new DonationPricingPageInputsModel({
          ...this.props.pricing,
          customAmount: amount,
          donationAmout: parseFloat(amount),
        })
      );
  };

  onClickPrice = (price: number) => {
    this.props.updateDonationPricingPageData &&
      this.props.updateDonationPricingPageData(
        new DonationPricingPageInputsModel({
          ...this.props.pricing,
          donationAmout: price,
          defaultPrice: price.toString(),
        })
      );
  };

  onDonationClick() {
    if (!this.props.onDonationClick) return;
    this.props.pricing.validateForCustomerFacing();
    this.props.updateDonationPricingPageData?.(
      new DonationPricingPageInputsModel({
        ...this.props.pricing,
      })
    );
    if (this.props.pricing.purchaseErrors.length > 0) return;
    this.props.onDonationClick();
  }

  get detailSection() {
    return this.props.detailSection;
  }

  get pricing() {
    return this.props.pricing;
  }

  onCustomAmountsChange(value: string, index: number) {
    if (!/^\d*\.?\d*$/.test(value as string)) {
      return;
    }
    const customAmounts = [...this.props.pricing.customAmounts];
    customAmounts[index] = value;
    this.props.updateDonationPricingPageData &&
      this.props.updateDonationPricingPageData(
        new DonationPricingPageInputsModel({
          ...this.props.pricing,
          customAmounts: customAmounts,
          defaultPrice: value,
          donationAmout: parseFloat(value),
          selectedCustomIndex: index,
        })
      );
  }

  get pricingSection() {
    const { pricing } = this.props;
    const {
      donationAmout,
      customAmount,
      defaultPrice,
      customAmounts,
      selectedCustomIndex,
    } = pricing;

    switch (
      DonationPricingOption[
        pricing.pricingOption as keyof typeof DonationPricingOption
      ]
    ) {
      case DonationPricingOption.Option1:
      case DonationPricingOption.Option2:
      case DonationPricingOption.Option3:
      case DonationPricingOption.Option4:
      case DonationPricingOption.Option5:
        const optionAmountErrorClassName = pricing.hasError("donationAmout")
          ? "error-border"
          : "";

        return (
          <FlexContainer
            spacing={15}
            padding="0px 0px 15px"
            fxWrap="wrap"
            justifyContent="center"
            data-field="pricingOption"
          >
            {DonationPricingOption[
              pricing.pricingOption as keyof typeof DonationPricingOption
            ]
              .split(", ")
              .map((price) => {
                const parsedPrice = parseFloat(
                  UtilityMethods.removeSpecialCharacters(price)
                );

                const defaultPrice = parseFloat(
                  UtilityMethods.removeSpecialCharacters(
                    this.props.pricing.defaultPrice || ""
                  )
                );

                const isActive =
                  (this.props.isPreview && defaultPrice === parsedPrice) ||
                  (!this.props.isPreview && donationAmout === parsedPrice);

                const isCustomActive =
                  customAmount === donationAmout.toString();

                if (price.trim() === "Custom") {
                  const errorClassName = pricing.hasError("customAmount")
                    ? "error-border"
                    : "";

                  return (
                    <PricingOption
                      key={price}
                      showInput={true}
                      selector={true}
                      value={customAmount}
                      onClick={() => this.onCustomAmountChange(customAmount)}
                      onChange={(value) => this.onCustomAmountChange(value)}
                      className={`adv-pricing-option ${errorClassName} ${
                        isCustomActive ? "isActive" : ""
                      }`}
                      selectedBgColor={
                        this.props.donationOptions
                          ? this.props.donationOptions.mainColors[0].color?.rgba
                          : "#2c3e50"
                      }
                      notSelectedBgColor={
                        this.props.donationOptions
                          ? this.props.donationOptions.mainColors[1].color?.rgba
                          : "#E7E9EC"
                      }
                      color={
                        this.props.donationOptions?.fontColors[2].color?.rgba
                      }
                      activeFontColor={
                        this.props.donationOptions?.fontColors[1].color?.rgba
                      }
                    />
                  );
                }

                return (
                  <PricingOption
                    onClick={() => this.onClickPrice(parsedPrice)}
                    className={`adv-pricing-option ${
                      !isCustomActive && isActive ? "isActive" : ""
                    }`}
                    key={price}
                    value={price}
                    selector={true}
                    selectedBgColor={
                      this.props.donationOptions
                        ? this.props.donationOptions.mainColors[0].color?.rgba
                        : "#2c3e50"
                    }
                    notSelectedBgColor={
                      this.props.donationOptions
                        ? this.props.donationOptions.mainColors[1].color?.rgba
                        : "#E7E9EC"
                    }
                    color={
                      this.props.donationOptions?.fontColors[2].color?.rgba
                    }
                    activeFontColor={
                      this.props.donationOptions?.fontColors[1].color?.rgba
                    }
                  />
                );
              })}
            {optionAmountErrorClassName && (
              <CustomLabel fontSize="12px" color="red">
                Please select an amount
              </CustomLabel>
            )}
          </FlexContainer>
        );

      case DonationPricingOption.CustomAll:
        return (
          <>
            <FlexContainer
              spacing={15}
              padding="0px 0px 15px"
              fxWrap="wrap"
              justifyContent="center"
              data-field="pricingOption"
            >
              {Array(6)
                .fill(0)
                .map((_, index) => {
                  const rawValue = customAmounts?.[index] || "";
                  const formattedValue =
                    rawValue && !isNaN(Number(rawValue))
                      ? `${Number(rawValue).toLocaleString("en-US")}`
                      : rawValue;
                  const isActive =
                    defaultPrice === rawValue &&
                    rawValue !== "" &&
                    selectedCustomIndex === index;

                  const errorClassName = pricing.hasError(
                    `customAmounts${index}` as keyof DonationPricingPageInputsModel
                  )
                    ? "error-border"
                    : "";

                  return (
                    <PricingOption
                      key={`custom-${index}`}
                      showInput={index === 5}
                      selector={true}
                      value={formattedValue}
                      onChange={(newValue) =>
                        this.onCustomAmountsChange(newValue, index)
                      }
                      onClick={() =>
                        this.onCustomAmountsChange(rawValue, index)
                      }
                      className={`adv-pricing-option ${errorClassName} ${
                        isActive ? "isActive" : ""
                      }`}
                      selectedBgColor={
                        this.props.donationOptions
                          ? this.props.donationOptions.mainColors[0].color?.rgba
                          : "#2c3e50"
                      }
                      notSelectedBgColor={
                        this.props.donationOptions
                          ? this.props.donationOptions.mainColors[1].color?.rgba
                          : "#E7E9EC"
                      }
                      color={
                        this.props.donationOptions?.fontColors[2].color?.rgba
                      }
                      activeFontColor={
                        this.props.donationOptions?.fontColors[1].color?.rgba
                      }
                    />
                  );
                })}
              {pricing.hasError(
                "customAmounts5" as keyof DonationPricingPageInputsModel
              ) && (
                <p className="error-message" style={{ marginTop: "10px" }}>
                  {pricing.getErrorMessage(
                    "customAmounts5" as keyof DonationPricingPageInputsModel
                  )}
                </p>
              )}
            </FlexContainer>
          </>
        );

      default:
        return null;
    }
  }

  get benefitSection() {
    if (
      (!this.pricing.canSelectOneBeneficiary &&
        !this.pricing.canSelectMultipleBeneficiaries) ||
      this.pricing.beneficiaries.length === 0
    )
      return null;
    const selectedBeneficiaryIds = this.pricing.selectedBeneficiaries.map(
      (beneficiary) => beneficiary.id
    );
    const hasError = this.pricing.hasError("selectedBeneficiaries");

    const handleSelectBeneficiary = (beneficiary: DonationBeneficiaryModel) => {
      const isSelected = selectedBeneficiaryIds.includes(beneficiary.id);
      if (isSelected) {
        const updatedSelectedBeneficiaries =
          this.pricing.selectedBeneficiaries.filter(
            (b) => b.id !== beneficiary.id
          );
        this.onChange("selectedBeneficiaries", updatedSelectedBeneficiaries);
      } else {
        const updatedSelectedBeneficiaries = [
          ...this.pricing.selectedBeneficiaries,
          beneficiary,
        ];
        this.onChange("selectedBeneficiaries", updatedSelectedBeneficiaries);
      }
    };

    return (
      <FlexContainer
        data-field="beneficiaries"
        fxLayout="column"
        alignItems="center"
      >
        <CustomLabel
          fontWeight="600"
          color="#2C3E50"
          textAlign="center"
          margin="40px auto 15px"
        >
          Your Donation Should Benefit
        </CustomLabel>
        <FlexContainer spacing={10} justifyContent="center" fxWrap="wrap">
          {this.pricing.beneficiaries.map((beneficiary, idx) => {
            return (
              <CustomDonationButton
                key={idx}
                className="adv-benefit-btn"
                bgColor={
                  selectedBeneficiaryIds.includes(beneficiary.id) &&
                  this.props.donationOptions
                    ? this.props.donationOptions.mainColors[0].color?.rgba
                    : this.props.donationOptions?.mainColors[1].color?.rgba
                }
                color={
                  selectedBeneficiaryIds.includes(beneficiary.id) &&
                  this.props.donationOptions
                    ? this.props.donationOptions?.fontColors[1].color?.rgba
                    : this.props.donationOptions?.fontColors[2].color?.rgba
                }
                onClick={() => handleSelectBeneficiary(beneficiary)}
              >
                {beneficiary.name}
              </CustomDonationButton>
            );
          })}
        </FlexContainer>
        {hasError && (
          <p className="error-message" style={{ marginTop: "10px" }}>
            {this.pricing.getErrorMessage("selectedBeneficiaries")}
          </p>
        )}
      </FlexContainer>
    );
  }

  get donationInMemory() {
    if (this.pricing.offerInMemoryOf === null) return null;
    if (!this.pricing.offerInMemoryOf && this.props.isPreview) return null;
    return (
      <div className="donation-chckbx-memory" data-field="offerInMemoryOf">
        <Input
          type="checkbox"
          value={this.pricing.offerInMemoryOf && !this.props.isPreview}
          onChange={(e) => {
            this.onChange("offerInMemoryOf", e.target.checked);
          }}
          label="Yes, I want to dedicate my donation in honor or memory of someone."
          chbxBgColor={`${this.props.donationOptions?.mainColors[0].color?.rgba} !important`}
        />
        {this.pricing.allowComments && this.pricing.offerInMemoryOf && (
          <div className="chkbx-list-wrapper" data-field="inMemoryOfName">
            {this.pricing.offerInMemoryOf && (
              <Input
                type="text"
                onChange={(e: any) => {
                  this.onChange("inMemoryOfName", e.target.value);
                }}
                value={this.pricing.inMemoryOfName}
                placeholder="Dedicate to..."
                error={this.pricing.hasError("inMemoryOfName")}
                borderColor={
                  this.props.donationOptions?.borderColors[0].color?.rgba
                }
              />
            )}
            {this.pricing.allowComments && this.pricing.offerInMemoryOf && (
              <div data-field="allowComments">
                <Input
                  type="textarea"
                  onChange={(e: any) => {
                    this.onChange("inMemoryOfComment", e.target.value);
                  }}
                  value={this.pricing.inMemoryOfComment}
                  placeholder="Comments..."
                  error={this.pricing.hasError("inMemoryOfComment")}
                  borderColor={
                    this.props.donationOptions?.borderColors[0].color?.rgba
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  get annualIncrease() {
    const { annualIncreasePercentage } = this.props.pricing;
    if (this.pricing.annualIncrease === null) return null;
    if (!this.pricing.annualIncrease && this.props.isPreview) return null;

    const percentages: number[] = [3, 5, 10, 15, 20];
    return (
      <div data-field="annualIncrease">
        <Input
          type="checkbox"
          onChange={(e) => this.onChange("annualIncrease", e.target.checked)}
          chbxBgColor={`${this.props.donationOptions?.mainColors[0].color?.rgba} !important`}
          label={
            <>
              <span>
                {this.pricing.annualIncreaseDescription}
                {/* Increase my monthly
                donation every year by <b>{annualIncreasePercentage}%.</b> I can
                change any time. */}
              </span>
            </>
          }
          value={this.pricing.annualIncrease && !this.props.isPreview}
        />
        {this.pricing.annualIncrease && !this.props.isPreview && (
          <div className="chkbx-list-wrapper">
            <FlexContainer spacing={9} padding="10px 0px 0px" fxWrap="wrap">
              {percentages.map((percentage, idx) => {
                return (
                  <CustomDonationButton
                    key={idx}
                    // pass it here
                    color={
                      percentage === annualIncreasePercentage
                        ? this.props.donationOptions?.fontColors[1].color?.rgba
                        : this.props.donationOptions?.fontColors[2].color?.rgba
                    }
                    className={`adv-increase-btn ${percentage === annualIncreasePercentage ? "selected" : ""}`}
                    // primary={percentage === this.state.annualIncreasePercentage}
                    onClick={() =>
                      this.onChange("annualIncreasePercentage", percentage)
                    }
                    bgColor={
                      percentage === annualIncreasePercentage
                        ? this.props.donationOptions
                          ? this.props.donationOptions.mainColors[0].color?.rgba
                          : "#2c3e50"
                        : this.props.donationOptions
                          ? this.props.donationOptions.mainColors[1].color?.rgba
                          : ""
                    }
                  >
                    {percentage}%
                  </CustomDonationButton>
                );
              })}
            </FlexContainer>
          </div>
        )}
      </div>
    );
  }

  get coverFeeSection() {
    if (this.pricing.coverProcessingCost === null) return null;
    if (!this.pricing.coverProcessingCost && this.props.isPreview) return null;

    return (
      <div data-field="coverProcessingCost">
        <Input
          type="checkbox"
          onChange={(e) => {
            this.onChange("coverProcessingCost", e.target.checked);
          }}
          label={this.pricing.coverProcessingCostDescription}
          value={this.pricing.coverProcessingCost && !this.props.isPreview}
          chbxBgColor={`${this.props.donationOptions?.mainColors[0].color?.rgba} !important`}
        />
      </div>
    );
  }

  get anonymousSection() {
    if (this.pricing.allowAnonymousDonation === null) return null;
    if (!this.pricing.allowAnonymousDonation && this.props.isPreview)
      return null;
    return (
      <FlexContainer data-field="allowAnonymousDonation">
        <Input
          type="checkbox"
          onChange={(e) => {
            this.onChange("allowAnonymousDonation", e.target.checked);
          }}
          label="Make my donation anonymous"
          value={
            this.props.pricing.allowAnonymousDonation && !this.props.isPreview
          }
          chbxBgColor={`${this.props.donationOptions?.mainColors[0].color?.rgba} !important`}
        />
      </FlexContainer>
    );
  }

  get donationTypeSection() {
    const { pricing } = this.props;
    const { oneTime } = pricing;

    if (
      DonationType[this.pricing.type as keyof typeof DonationType] ===
      DonationType.OneTime
    )
      return null;
    return (
      <FlexContainer data-field="type" fxLayout="column" alignItems="center">
        <CustomLabel
          fontWeight="600"
          color="#2C3E50"
          textAlign="center"
          margin="35px auto 15px"
        >
          {oneTime ? "  Make it Monthly" : ""}
        </CustomLabel>
        <FlexContainer spacing={10}>
          <CustomDonationButton
            className={!oneTime ? "selected" : ""}
            onClick={() => this.onChange("oneTime", false)}
            color={
              !oneTime
                ? this.props.donationOptions?.fontColors[1].color?.rgba
                : this.props.donationOptions?.fontColors[2].color?.rgba
            } // pass it here
            bgColor={
              !oneTime
                ? this.props.donationOptions
                  ? this.props.donationOptions.mainColors[0].color?.rgba
                  : "#2c3e50"
                : this.props.donationOptions
                  ? this.props.donationOptions.mainColors[1].color?.rgba
                  : ""
            }
          >
            Monthly
          </CustomDonationButton>
          <CustomDonationButton
            className={oneTime ? "selected" : ""}
            onClick={() => this.onChange("oneTime", true)}
            color={
              oneTime
                ? this.props.donationOptions?.fontColors[1].color?.rgba
                : this.props.donationOptions?.fontColors[2].color?.rgba
            } // pass it here
            bgColor={
              oneTime
                ? this.props.donationOptions
                  ? this.props.donationOptions.mainColors[0].color?.rgba
                  : "#2c3e50"
                : this.props.donationOptions
                  ? this.props.donationOptions.mainColors[1].color?.rgba
                  : ""
            }
          >
            One Time
          </CustomDonationButton>
        </FlexContainer>

        {pricing.descriptionEditorRawContent && (
          <FlexContainer
            alignItems="center"
            spacing={0}
            className="pointing-upwards-icon"
          >
            <img src={PointingUpwards} />
            {/* <CustomLabel fontSize="14px">{pricing.description}</CustomLabel> */}
            <CustomLabel
              dangerouslySetInnerHTML={{
                __html: UtilityMethods.getEditorHtmlContent(
                  pricing.descriptionEditorRawContent
                ),
              }}
            ></CustomLabel>
          </FlexContainer>
        )}
      </FlexContainer>
    );
  }

  get button() {
    const {
      oneTime,
      customAmount,
      donationAmout,
      annualIncrease,
      type,
      pricingOption,
    } = this.props.pricing;
    const amount =
      donationAmout > 0
        ? donationAmout
        : pricingOption !== ("CustomAll" as keyof DonationPricingOption)
          ? customAmount
          : "";
    const isMonthly =
      !oneTime &&
      DonationType[type as keyof typeof DonationType] ===
        DonationType.OneTimeOrMonthly;
    const isValidAmount = validateDecimalValue(amount.toString());
    const hasError =
      this.pricing.hasError("donationAmout") ||
      this.pricing.hasError("customAmount");
    return (
      <>
        <DonationPayButton
          onClick={() => this.onDonationClick()}
          bgColor={
            this.props.donationOptions
              ? this.props.donationOptions.mainColors[0].color?.rgba
              : "#2c3e50"
          }
          className="break-word purchase-donation-btn"
          color={this.props.donationOptions?.fontColors[1].color?.rgba}
        >
          {isValidAmount
            ? `Donate $${UtilityMethods.numberWithCommas(parseFloat(amount.toString()))}`
            : "Donate"}{" "}
          {isMonthly ? " Monthly " : ""}
          {isValidAmount &&
            !oneTime &&
            annualIncrease &&
            this.annualIncreaseDescription(parseFloat(amount.toString()))}
        </DonationPayButton>
        {hasError && (
          <p className="error-message" style={{ marginTop: "10px" }}>
            {this.pricing.getErrorMessage("donationAmout") ||
              this.pricing.getErrorMessage("customAmount")}
          </p>
        )}
      </>
    );
  }

  get nextYear() {
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    return UtilityMethods.formatDateInString(oneYearFromNow);
  }

  annualIncreaseDescription(amount: number) {
    const { annualIncreasePercentage } = this.props.pricing;

    if (annualIncreasePercentage === 0) return "";
    const percentageAmount = (annualIncreasePercentage / 100) * amount;
    const totalAmount = amount + percentageAmount;

    return `Increase by $${annualIncreasePercentage}% to $${totalAmount} starting ${this.nextYear} and every year thereafter unless cancelled.`;
  }

  get paidToText() {
    if (!this.detailSection.donationPaidTo) return null;
    return (
      <>
        <CustomLabel fontSize="14px" margin="20px 0px 0px" textAlign="left">
          Your donation will be paid to{" "}
          <b className="text-nowrap">{this.detailSection.donationPaidTo}</b>.{" "}
          {this.detailSection.tax && (
            <span className="text-nowrap">
              Tax ID <b>{this.detailSection.taxId}</b>
            </span>
          )}
        </CustomLabel>
      </>
    );
  }

  get taxIdSection() {
    if (!this.detailSection.donationPaidTo || !this.detailSection.taxId)
      return null;
    return (
      <>
        <CustomLabel fontSize="14px" margin="20px 0px 0px" textAlign="left">
          <b>{this.detailSection.donationPaidTo}</b> is a registered{" "}
          <b>{this.detailSection.taxExemptSection}</b> organization.
          <br /> Donations are {this.detailSection.tax ? "" : "not"} tax
          deductible.
        </CustomLabel>
      </>
    );
  }

  get mediaSection() {
    const { medias } = this.props.detailSection;
    return (
      <CarouselComponent
        media={medias}
        className={`donation-ad-carousel ${medias.length < 2 ? "single-media-caraousel" : ""}`}
      />
    );
  }

  donationContent(data: DonationPricingPageInputsModel) {
    const { donationOptions } = this.props;
    const hasMedia = donationOptions && donationOptions?.media.length > 0;
    const imagePlacement = donationOptions?.imagePlacement;
    const isMediaAvailable =
      hasMedia || this.props.detailSection.medias.length > 0;
    const chooseAmountMargin =
      !data.title && !data.subTitle && isMediaAvailable
        ? "30px auto 15px"
        : "15px auto 15px";
    const carouselWithShowOnMobile =
      hasMedia &&
      this.props.donationOptions &&
      !this.props.donationOptions?.imageHideOnMobile;
    return (
      <AdvertisingBlockWrapper
        padding="10px 20px"
        className={`donation-ad-block ${hasMedia && imagePlacement === ImagePlacementTypes.Background ? "bg-image-ad-block" : ""} 
        ${carouselWithShowOnMobile ? "no-padding-small-devices" : ""}
                `}
        border={`1px solid ${donationOptions?.borderColors[0].color?.rgba}`}
        bgColor={
          this.props.donationOptions &&
          donationOptions?.backgroundColors[0].color?.rgba
        }
        minHeight="150px"
        width="auto"
        // maxWidth="421px"
        maxWidth="480px"
        height="auto"
        placementSlider={donationOptions?.placementSlider}
      >
        {/* {hasMedia && imagePlacement === ImagePlacementTypes.Left && (
                    <div className="donation-left-right-image">{this.renderImage(donationOptions.media)}</div>
                )} */}
        {/* {hasMedia && <div className="donation-bg-image">{this.renderImage(donationOptions.media)}</div>} */}
        {/* {this.props.donationOptions &&
          !this.props.donationOptions.imageHideOnMobile &&
          this.props.donationOptions.media.length > 0 && (
            <img
              className="carousel-dummy-image"
              src={this.props.donationOptions.media[0].fullURL}
              alt={`Slide`}
            />
          )} */}
        {hasMedia && (
          <div className="bg-img-ss">
            {this.renderImage(donationOptions.media)}
          </div>
        )}
        <FlexContainer
          fxLayout="column"
          spacing={15}
          alignItems="center"
          size={0}
        >
          {carouselWithShowOnMobile && (
            <div className="donation-image-wrapper">
              <img
                className={`donation-carousel-img`}
                src={this.props.donationOptions?.media[0].fullURL}
                alt={`Slide`}
              />
            </div>
          )}
          {this.mediaSection}
          {data.titleEditorRawContent && (
            <div data-field="title" className="donation-title-padding">
              {/* <CustomLabel
                fontSize="22px"
                fontWeight="600"
                color={theme.palette.common.black}
                lineHeight="24px"
                textAlign="center"
                margin="25px auto 0px"
              >
                {data.title}
              </CustomLabel> */}
              <CustomLabel
                margin={
                  this.props.detailSection.medias.length < 2
                    ? "25px auto 0px"
                    : "40px auto 0px"
                }
                dangerouslySetInnerHTML={{
                  __html: UtilityMethods.getEditorHtmlContent(
                    data.titleEditorRawContent
                  ),
                }}
              ></CustomLabel>
            </div>
          )}
          {data.subTitleEditorRawContent && (
            <div data-field="subTitle" className="donation-title-padding">
              {/* <CustomLabel
                fontSize="14px"
                textAlign="center"
                margin={!data.title ? "25px auto 0px" : "0px auto"}
                padding="0px 10px"
              >
                {data.subTitle}
              </CustomLabel> */}
              <CustomLabel
                margin={!data.title ? "25px auto 0px" : "0px auto"}
                dangerouslySetInnerHTML={{
                  __html: UtilityMethods.getEditorHtmlContent(
                    data.subTitleEditorRawContent
                  ),
                }}
              ></CustomLabel>
            </div>
          )}
          <div>
            <FlexContainer
              fxLayout="column"
              alignItems="center"
              padding="0px 25px 30px"
              className="donation-choose-amount"
            >
              <div>
                <CustomLabel
                  fontWeight="600"
                  color="#2C3E50"
                  textAlign="center"
                  margin={chooseAmountMargin}
                >
                  Choose an Amount
                </CustomLabel>
              </div>
              <div>{this.pricingSection}</div>
              {/* <Input
                        type="textarea"
                        onChange={() => {}}
                        value={data.message}
                        placeholder="Donate $250.00 or more and receive a complementary gym bag as our thanks!"
                    /> */}
              {this.props.pricing.messageEditorRawContent && (
                <div data-field="message">
                  {/* <CustomLabel fontSize={"14px"}>
              {this.props.pricing.message}
            </CustomLabel> */}
                  <CustomLabel
                    dangerouslySetInnerHTML={{
                      __html: UtilityMethods.getEditorHtmlContent(
                        data.messageEditorRawContent
                      ),
                    }}
                  ></CustomLabel>
                </div>
              )}

              <div className="full-width">{this.donationTypeSection}</div>
              <div className="full-width">{this.benefitSection}</div>
              <div className="full-width">{this.donationInMemory}</div>
              {!this.pricing.oneTime && (
                <div className="full-width">{this.annualIncrease}</div>
              )}
              <div className="full-width">{this.coverFeeSection}</div>
              <div className="full-width">{this.anonymousSection}</div>
              <div className="full-width">{this.button}</div>
              <div className="full-width">{this.paidToText}</div>
              <div className="full-width">{this.taxIdSection}</div>
              {/* {hasMedia && imagePlacement === ImagePlacementTypes.Right && (
                    <div className="donation-left-right-image">{this.renderImage(donationOptions.media)}</div>
                )} */}
            </FlexContainer>
          </div>
        </FlexContainer>
      </AdvertisingBlockWrapper>
    );
  }

  renderImage = (media: MediaModel[]) => {
    const { donationOptions } = this.props;
    return (
      <img
        className={`carousel-dummy-image donation-carousel-img ${donationOptions?.imageFormat === "Original" ? "carousel-original" : ""}`}
        src={media[0].fullURL}
        alt={`Slide`}
      />
    );
  };

  render(): React.ReactNode {
    const { pricing: data, donationOptions } = this.props;
    const imagePlacement = donationOptions?.imagePlacement;
    const hasMedia = donationOptions && donationOptions?.media.length > 0;

    return (
      <AdvertisingBlockMainWrapper
        ref={this.previewRef}
        defaultFont={this.props.donationOptions?.fontColors[0].color?.rgba}
        primaryButton={this.props.donationOptions?.fontColors[1].color?.rgba}
      >
        {donationOptions ? (
          <FlexContainer
            className={`donation-img-wrapper ${hasMedia && imagePlacement === ImagePlacementTypes.Background ? "donation-img-bg-wrapper" : ""} ${
              imagePlacement === ImagePlacementTypes.Left
                ? "left-layout"
                : imagePlacement === ImagePlacementTypes.Right
                  ? "right-layout"
                  : ""
            }`}
            bgColor={
              donationOptions
                ? donationOptions.backgroundColors[1].color?.rgba
                : ""
            }
          >
            {hasMedia && imagePlacement === ImagePlacementTypes.Left && (
              <div
                className={`donation-left-right-image ${donationOptions?.imageFormat === "Original" ? "carousel-fit-left" : ""}`}
              >
                {this.renderImage(donationOptions.media)}
              </div>
            )}
            {hasMedia && imagePlacement === ImagePlacementTypes.Background && (
              <div className="donation-bg-image">
                {this.renderImage(donationOptions.media)}
              </div>
            )}
            <MembershipRightBar
              bgColor={donationOptions.backgroundColors[1].color?.rgba}
              borderRadius="8px"
              padding="0px"
              // width="50%" // creating issue for donation with background image, any way we have max width of 480px */
              className={`margin-0-auto donation-layouts ${
                imagePlacement === ImagePlacementTypes.Left
                  ? ""
                  : imagePlacement === ImagePlacementTypes.Right
                    ? ""
                    : "default-layout"
              }`}
              height="auto"
            >
              {this.donationContent(data)}
            </MembershipRightBar>
            {hasMedia && imagePlacement === ImagePlacementTypes.Right && (
              <div
                className={`donation-left-right-image ${donationOptions?.imageFormat === "Original" ? "carousel-fit-right" : ""}`}
              >
                {this.renderImage(donationOptions.media)}
              </div>
            )}
          </FlexContainer>
        ) : (
          <div>
            <MembershipRightBar
              borderRadius="8px"
              className="margin-0-auto float-block"
              height="calc(100% - 60px)"
              width="35%"
            >
              {this.donationContent(data)}
            </MembershipRightBar>
          </div>
        )}
      </AdvertisingBlockMainWrapper>
    );
  }
}

export default DonationAdvertisingBlock;
